/**
 * Notification dialog entities — extracted from legacy/index.ts
 *
 * Contains: CancelPanicDialog, OfficerAssignedDialogInfo, PanicDialogInfo,
 * CaseReportSubmittedDialogInfo, and their supporting functions.
 */

import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { ModuleType } from '@/constants/codes';
import { hasValue } from '@/utils/legacy-utils';
import { getOfficerLabel, getCurrentUserId, getModuleType, getOfficerPluralLabel } from '@/utils/auth';
import { getCurrentTimeUTC } from '@/utils/date-time';
import { renderImperativeModal, ImperativeModalHandle } from '@/utils/imperative-modal';

// Window globals — accessed lazily to avoid capturing undefined at module load time
const w = window as any;
const getMDTActiveCallCardId = () => w.getMDTActiveCallCardId?.();
const addMDTActiveCallCard = (...args: any[]) => w.addMDTActiveCallCard?.(...args);
const goToInProgressScreen = () => w.goToInProgressScreen?.();
const isWindowsAppSdkApp = () => w.isWindowsAppSdkApp;
const notifyApp = (...args: any[]) => w.notifyApp?.(...args);
const selectModuleCallback = (...args: any[]) => w.selectModuleCallback?.(...args);
const isAtLeastVersionNumber = (...args: any[]) => w.isAtLeastVersionNumber?.(...args);
const showCaseReport = (...args: any[]) => w.showCaseReport?.(...args);

// ============================================================================
// cancel-panic-dialog-info.ts
// ============================================================================

export let _cancelPanicContainer: HTMLElement | null = null;
export let _cancelPanicIsOpen = false;

export class CancelPanicDialog {
    callCardId: number = null;
    activeCallCardPriorToPanicCall: number = null;

    show() {
        _cancelPanicIsOpen = true;
        this._render();
    }

    _render() {
        if (!_cancelPanicContainer) {
            _cancelPanicContainer = document.createElement('div');
            document.body.appendChild(_cancelPanicContainer);
        }

        (window as any).OrnaReact.renderComponent(
            (window as any).CancelPanicModalReact,
            {
                open: _cancelPanicIsOpen,
                onAllPressed: function () {
                    cancelPanicDialog.cancelPanicCall();
                },
                onDismiss: function () {
                    _cancelPanicIsOpen = false;
                    cancelPanicDialog._render();
                }
            },
            _cancelPanicContainer
        );
    }

    setPanicDialogCallCardId(callCardId: number) {
        this.callCardId = callCardId;
    }

    loadActiveCallCardPriorToPanicCall() {
        this.activeCallCardPriorToPanicCall = getMDTActiveCallCardId();
    }

    //Wait up to 15 seconds for panic call to load in case of a slow connection.
    cancelPanicCall(timeoutAttempt = 0) {

        //After 30 attempts at 0.5 seconds each, fail.
        if (timeoutAttempt > 30) {
            App.showPopupNotification("Failed to cancel panic call.", 'error');
            _cancelPanicIsOpen = false;
            cancelPanicDialog._render();
            return;
        }

        if (!hasValue(App.currentCC) || App.currentCC.id !== cancelPanicDialog.callCardId) {
            setTimeout(cancelPanicDialog.cancelPanicCall, 500, timeoutAttempt + 1)
            return
        }

        if (App.currentCC.primaryOfficer == getCurrentUserId()) {
            const gridOfficers = App.callCardController?.officers;

            if (!gridOfficers || gridOfficers.length === 0) {
                App.showPopupNotification(`No ${getOfficerPluralLabel().toLowerCase()} on this panic call.`, 'error');
                return;
            } else if (gridOfficers.length > 1) {
                App.showPopupNotification(`More than one ${getOfficerLabel().toLowerCase()} on this panic call.`, 'error');
                return;
            }

            // Update the officer's cleared time
            const clearedTime = getCurrentTimeUTC();
            gridOfficers[0].cleared = clearedTime;

            // Set disposition code - use API to get the correct disposition codes
            api.listCallDispositions(result => {
                const falseAlarmDisposition = result.callDispositionList.find(d =>
                    d.dispositionValue === 'False Alarm'
                );

                const dispositionCode = falseAlarmDisposition?.dispositionCode || 16;

                App.currentCC.callDisposition = dispositionCode;

                // Continue with the rest of the logic
                cancelPanicDialog.continueClosingPanicCall();
            }, errorResult => {
                App.showPopupNotification("Error getting call disposition data.", 'error');
                // Use fallback
                App.currentCC.callDisposition = 16;
                cancelPanicDialog.continueClosingPanicCall();
            });
        } else {
            App.showPopupNotification(`${getOfficerLabel()} must be primary ${getOfficerLabel().toLowerCase()} to close call from this dialog.`, 'error');
        }
    }

    continueClosingPanicCall(): void {
        //Update previous call. If user was previously on an active call, go to it.
        let closeCCCallback: Function = null
        if (hasValue(cancelPanicDialog.activeCallCardPriorToPanicCall)) {
            const moduleType: string = getModuleType().toUpperCase();

            if (moduleType === 'MDT') {
                closeCCCallback = () => {
                    if (cancelPanicDialog.activeCallCardPriorToPanicCall) {
                        window.OrnaReact.navigate.toCallCard(cancelPanicDialog.activeCallCardPriorToPanicCall, 'MDT');
                        setTimeout(() => {
                            addMDTActiveCallCard(cancelPanicDialog.activeCallCardPriorToPanicCall, true);
                        }, 0);
                    } else {
                        goToInProgressScreen();
                    }
                }
            }
        }
        App.currentCCCntr.closeCC(closeCCCallback);
    }
}

export let cancelPanicDialog: CancelPanicDialog = new CancelPanicDialog()


// ============================================================================
// officer-assigned-dialog-info.ts
// ============================================================================

export let previousOfficerAssignedDialogInfo = null;
export let showRecentOfficerAssignedDialogOnPageLoaded = false;

export class OfficerAssignedDialogInfo {
    constructor(result: any) {
        if (result !== undefined && result !== null) {
            for (let k in result) {
                if (this[k] !== undefined) {
                    this[k] = result[k];
                }
            }
        }
    }

    callCard: number = null;
    eventId: string = null;
    location: string = null;
    natureOfCallString: string = null;
    timeDialogShown: number = null
}

export function isShowingRecentOfficerAssignedDialogOnPageLoaded(): boolean {
    return showRecentOfficerAssignedDialogOnPageLoaded;
}

export function setShowingRecentOfficerAssignedDialogOnPageLoaded(enabled: boolean) {
    showRecentOfficerAssignedDialogOnPageLoaded = enabled;
}

export let _officerAssignedContainer: HTMLElement | null = null;
export let _officerAssignedCallCardId: number = null;
export let _officerAssignedState: { isOpen: boolean, eventId: string, natureOfCall: string, callLocation: string } | null = null;

export function _renderOfficerAssignedDialog() {
    if (!_officerAssignedContainer || !_officerAssignedState) return;

    (window as any).OrnaReact.renderComponent(
        (window as any).OfficerAssignedDialogReact,
        {
            isOpen: _officerAssignedState.isOpen,
            eventId: _officerAssignedState.eventId,
            natureOfCall: _officerAssignedState.natureOfCall,
            callLocation: _officerAssignedState.callLocation,
            onViewCall: function () {
                if (_officerAssignedState) _officerAssignedState.isOpen = false;
                _renderOfficerAssignedDialog();
                const callCardId = _officerAssignedCallCardId;
                showPageMDTCallCard(callCardId);
            },
            onDismiss: function () {
                if (_officerAssignedState) _officerAssignedState.isOpen = false;
                _renderOfficerAssignedDialog();
            }
        },
        _officerAssignedContainer
    );
}

export function showOfficerAssignedDialog(officerAssignedDialogInfo: OfficerAssignedDialogInfo) {
    _officerAssignedCallCardId = officerAssignedDialogInfo.callCard;

    if (_officerAssignedState) {
        _officerAssignedState.eventId = officerAssignedDialogInfo.eventId;
        _officerAssignedState.natureOfCall = officerAssignedDialogInfo.natureOfCallString;
        _officerAssignedState.callLocation = officerAssignedDialogInfo.location;
        _officerAssignedState.isOpen = true;
        _renderOfficerAssignedDialog();
        return;
    }

    if (!_officerAssignedContainer) {
        _officerAssignedContainer = document.createElement('div');
        document.body.appendChild(_officerAssignedContainer);
    }

    _officerAssignedState = {
        isOpen: true,
        eventId: officerAssignedDialogInfo.eventId,
        natureOfCall: officerAssignedDialogInfo.natureOfCallString,
        callLocation: officerAssignedDialogInfo.location
    };

    _renderOfficerAssignedDialog();
}

export function showOfficerAssignedDialogAndNotify(officerAssignedDialogInfo: OfficerAssignedDialogInfo) {
    officerAssignedDialogInfo.timeDialogShown = new Date().getTime();
    previousOfficerAssignedDialogInfo = officerAssignedDialogInfo;

    showOfficerAssignedDialog(officerAssignedDialogInfo);

    if (isWindowsAppSdkApp() && isAtLeastVersionNumber(2, 0, 5)) {
        //Windows app only, includes sounds. iOS and Android notifications sent from the backend.
        //Windows app prior to 2.0.5 did not include sound as part of the app.
        notifyApp("assignedCallCard");
    } else {
        if (hasValue(App.sndFire)) {
            App.sndFire.play().catch(() => {});
        } else {
            console.log("Error: sndFire not found");
        }
    }
}

//This function resolves an issue where frequently, but not always, refreshing the page appears to cause the modal dialog to hide.
export function showRecentOfficerAssignedDialogIfNotVisibleWithoutSound() {
    setShowingRecentOfficerAssignedDialogOnPageLoaded(false);
    const isVisible = _officerAssignedState && _officerAssignedState.isOpen;
    if (!isVisible && hasValue(previousOfficerAssignedDialogInfo)) {
        const currentTimeMillis = new Date().getTime();
        if (hasValue(previousOfficerAssignedDialogInfo.timeDialogShown) && (currentTimeMillis - previousOfficerAssignedDialogInfo.timeDialogShown < 5000)) {
            showOfficerAssignedDialog(previousOfficerAssignedDialogInfo);
        }
    }
}

function showPageCallCard(callCardId: number, moduleType: 'MDT' | 'CAD'): void {
    const moduleTypeLower = moduleType.toLowerCase() as 'mdt' | 'cad';
    if (App.getModuleTypeFromURL() === ModuleType[moduleTypeLower]) {
        window.OrnaReact.navigate.toCallCard(callCardId, moduleType);
    } else {
        api.selectModule(moduleTypeLower, result => {
            selectModuleCallback(result);
            window.OrnaReact.navigate.toCallCard(callCardId, moduleType);
        }, () => { //error callback
        });
    }
}

export function showPageMDTCallCard(callCardId: number): void {
    showPageCallCard(callCardId, 'MDT');
}

//function called below AND called by UWP App
export function btnOfficerAssignedCallCardDialogViewCallClicked() {
    if (_officerAssignedState) {
        _officerAssignedState.isOpen = false;
        _renderOfficerAssignedDialog();
    }
    const callCardId = _officerAssignedCallCardId;
    showPageMDTCallCard(callCardId);
}


// ============================================================================
// panic-dialog-info.ts
// ============================================================================

export class PanicDialogInfo {
    constructor(result: any) {
        if (result !== undefined && result !== null) {
            for (let k in result) {
                if (this[k] !== undefined) {
                    this[k] = result[k];
                }
            }
        }
    }

    callCard: number = null;
    eventId: string = null;
    location: string = null;
    natureOfCallString: string = null;
    timeDialogShown: number = null
}

export let _panicDialogContainer: HTMLElement | null = null;
export let _panicCallCardId: number = null;
export let _panicDialogState: { isOpen: boolean, eventId: string, natureOfCall: string, callLocation: string } | null = null;

export function _renderPanicDialog() {
    if (!_panicDialogContainer || !_panicDialogState) return;

    (window as any).OrnaReact.renderComponent(
        (window as any).PanicDialogReact,
        {
            isOpen: _panicDialogState.isOpen,
            eventId: _panicDialogState.eventId,
            natureOfCall: _panicDialogState.natureOfCall,
            callLocation: _panicDialogState.callLocation,
            onViewCall: function () {
                if (_panicDialogState) _panicDialogState.isOpen = false;
                _renderPanicDialog();
                const callCardId = _panicCallCardId;
                stopPanicSounds();
                showPageCADCallCard(callCardId);
            },
            onDismiss: function () {
                if (_panicDialogState) _panicDialogState.isOpen = false;
                _renderPanicDialog();
                stopPanicSounds();
            }
        },
        _panicDialogContainer
    );
}

export function showPanicDialog(panicDialogInfo: PanicDialogInfo) {
    _panicCallCardId = panicDialogInfo.callCard;

    if (_panicDialogState) {
        _panicDialogState.eventId = panicDialogInfo.eventId;
        _panicDialogState.natureOfCall = panicDialogInfo.natureOfCallString;
        _panicDialogState.callLocation = panicDialogInfo.location;
        _panicDialogState.isOpen = true;
        _renderPanicDialog();
        return;
    }

    if (!_panicDialogContainer) {
        _panicDialogContainer = document.createElement('div');
        document.body.appendChild(_panicDialogContainer);
    }

    _panicDialogState = {
        isOpen: true,
        eventId: panicDialogInfo.eventId,
        natureOfCall: panicDialogInfo.natureOfCallString,
        callLocation: panicDialogInfo.location
    };

    _renderPanicDialog();
}

export function stopPanicSounds() {
    if (isWindowsAppSdkApp() && isAtLeastVersionNumber(2, 0, 5)) {
        //Windows app only. iOS and Android notifications sent from the backend.
        notifyApp("stopAlertSounds");
    } else {
        App.stopSndPanic();
    }
}

export let playPanicSound2Interval = null
export function showPanicDialogAndNotify(panicDialogInfo: PanicDialogInfo) {
    panicDialogInfo.timeDialogShown = new Date().getTime();

    showPanicDialog(panicDialogInfo);
    if (isWindowsAppSdkApp() && isAtLeastVersionNumber(2, 0, 5)) {
        //Windows app only, includes sounds. iOS and Android notifications sent from the backend.
        notifyApp("panicNotification");
    } else {
        if (hasValue(App.sndPanic)) {
            App.sndPanic.play().catch(() => {});
        } else {
            console.log("Error: sndFire not found");
        }
    }
}

export function showPageCADCallCard(callCardId: number): void {
    showPageCallCard(callCardId, 'CAD');
}

//function called below TODO: called by UWP App?
export function btnPanicCallCardDialogViewCallClicked() {
    if (_panicDialogState) {
        _panicDialogState.isOpen = false;
        _renderPanicDialog();
    }
    const callCardId = _panicCallCardId;
    showPageCADCallCard(callCardId);
    stopPanicSounds();
}


// ============================================================================
// case-report-submitted-dialog-info.ts
// ============================================================================

export class CaseReportSubmittedDialogInfo {
    constructor(result: any) {
        if (result !== undefined && result !== null) {
            for (let k in result) {
                if (this[k] !== undefined) {
                    this[k] = result[k];
                }
            }
        }
    }

    caseReportId: number = null;
    caseId: string = null;
}

export let _caseReportSubmittedHandle: ImperativeModalHandle | null = null;
export let _caseReportSubmittedCaseReportId: number = null;
export let _caseReportSubmittedIsOpen = false;

export function showCaseReportSubmittedDialog(caseReportSubmittedDialogInfo: CaseReportSubmittedDialogInfo) {
    _caseReportSubmittedCaseReportId = caseReportSubmittedDialogInfo.caseReportId;

    const message = `Case Report ${caseReportSubmittedDialogInfo.caseId} has been submitted for approval.`;

    // Clean up previous instance if it exists
    if (_caseReportSubmittedHandle) {
        _caseReportSubmittedHandle.destroy();
        _caseReportSubmittedHandle = null;
    }

    _caseReportSubmittedIsOpen = true;

    _renderCaseReportSubmittedDialog(message);

    if (hasValue(App.sndCCUpdate)) {
        App.sndCCUpdate.play().catch(() => {});
    }
}

export function _renderCaseReportSubmittedDialog(message: string) {
    const props = {
        isOpen: _caseReportSubmittedIsOpen,
        message: message,
        onConfirm: function () {
            _caseReportSubmittedIsOpen = false;
            const caseReportId = _caseReportSubmittedCaseReportId;
            _closeCaseReportSubmittedReact();
            showCaseReport(caseReportId);
        },
        onCancel: function () {
            _caseReportSubmittedIsOpen = false;
            _closeCaseReportSubmittedReact();
        }
    };

    if (_caseReportSubmittedHandle) {
        _caseReportSubmittedHandle.update(props);
    } else {
        _caseReportSubmittedHandle = renderImperativeModal(
            (window as any).CaseReportSubmittedDialogReact,
            props
        );
    }
}

export function _closeCaseReportSubmittedReact() {
    if (_caseReportSubmittedHandle) {
        _caseReportSubmittedHandle.destroy();
        _caseReportSubmittedHandle = null;
    }
}

//function called below AND called by UWP App
export function btnCaseReportSubmittedForSupervisorReviewDialogViewCaseReportClicked() {
    _caseReportSubmittedIsOpen = false;
    _closeCaseReportSubmittedReact();
    showCaseReport(_caseReportSubmittedCaseReportId);
}


// ============================================================================
// Window registrations
// ============================================================================

// Dialog types
(window as any).CancelPanicDialog = typeof CancelPanicDialog !== 'undefined' ? CancelPanicDialog : undefined;
(window as any).PanicDialogInfo = typeof PanicDialogInfo !== 'undefined' ? PanicDialogInfo : undefined;
(window as any).OfficerAssignedDialogInfo = typeof OfficerAssignedDialogInfo !== 'undefined' ? OfficerAssignedDialogInfo : undefined;
(window as any).CaseReportSubmittedDialogInfo = typeof CaseReportSubmittedDialogInfo !== 'undefined' ? CaseReportSubmittedDialogInfo : undefined;

// Panic / officer-assigned dialog functions
(window as any).stopPanicSounds = stopPanicSounds;
(window as any).showPanicDialog = showPanicDialog;
(window as any).showPanicDialogAndNotify = showPanicDialogAndNotify;
(window as any).showPageCADCallCard = showPageCADCallCard;
(window as any).showPageMDTCallCard = showPageMDTCallCard;
(window as any).btnPanicCallCardDialogViewCallClicked = btnPanicCallCardDialogViewCallClicked;
(window as any).showOfficerAssignedDialogAndNotify = showOfficerAssignedDialogAndNotify;
(window as any).isShowingRecentOfficerAssignedDialogOnPageLoaded = isShowingRecentOfficerAssignedDialogOnPageLoaded;
(window as any).setShowingRecentOfficerAssignedDialogOnPageLoaded = setShowingRecentOfficerAssignedDialogOnPageLoaded;
(window as any).showRecentOfficerAssignedDialogIfNotVisibleWithoutSound = showRecentOfficerAssignedDialogIfNotVisibleWithoutSound;

// Case report submitted dialog functions
(window as any).showCaseReportSubmittedDialog = showCaseReportSubmittedDialog;
(window as any).btnCaseReportSubmittedForSupervisorReviewDialogViewCaseReportClicked = btnCaseReportSubmittedForSupervisorReviewDialogViewCaseReportClicked;
